import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { TabPanel, TabView } from 'primereact/tabview';
import Container from 'react-bootstrap/Container';
import { useAuth } from '../../contexts/AuthContext';
import usePageAccessRedirect from '../../hooks/usePageAccessRedirect';
import AccessGate from '../AccessGate';
import AccessState from '../AccessState';
import { canEnterFlagSystem, flagAccount, flagError } from '../../utils/flagSystem';
import { isFlagSystemDisabledError } from '../../utils/flagSystemCapability';
import { useCustomerFlagSystemCapability } from '../../contexts/FlagSystemCapabilityContext';
import './FlagSystem.css';

export const Unavailable = ({ children }) => <div className="flag-unavailable" role="status">
  <strong>BACKEND NOT YET AVAILABLE</strong><p className="mb-0 mt-2">{children}</p>
</div>;
export const FlagPage = ({ title, actions, children, className = '', containerFluid }) => <Card className={`flag-system mt-5 mx-3 mx-md-5 ${className}`}>
  <Container fluid={containerFluid}>
    <div className="flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
      <h4 className="mt-2 mb-1">{title}</h4><div className="flex gap-2 flex-wrap">{actions}</div>
    </div>
    {children}
  </Container>
</Card>;
export const Field = ({ id, label, error, children, hint }) => <div className="flag-field">
  <label htmlFor={id}>{label}</label>{children}
  {hint && <small className="text-color-secondary">{hint}</small>}
  {error && <small id={`${id}-error`} className="flag-error" role="alert">{error}</small>}
</div>;
export const Feedback = ({ error, message, warning }) => <div className="mb-3" aria-live="polite">
  {error && <Message severity="error" text={error} />}
  {message && <Message severity="success" text={message} />}
  {warning && <Message severity="warn" text={warning} />}
</div>;
export const FlagCapabilityState = ({ capability }) => {
  if (capability.status === 'loading' || capability.status === 'idle') return <div role="status" aria-label="Checking Flag System access">
    <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
    <small className="text-color-secondary">Checking Flag System access...</small>
  </div>;
  return <div className="flex align-items-center flex-wrap gap-2 my-3">
    <Message severity={capability.status === 'error' ? 'error' : 'warn'}
      text={capability.error || 'The Flag System is not enabled for your customer.'} />
    {capability.canRetry && <Button label={capability.status === 'error' ? 'Retry Flag System access' : 'Check Flag System access again'}
      className="p-button-text p-button-sm" disabled={capability.refreshing} onClick={capability.refetch} />}
  </div>;
};

export const useRemoteResource = (loader, onAccessError) => {
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({ loader: null, data: null, error: '', loading: Boolean(loader) });
  useEffect(() => {
    let current = true;
    if (!loader) {
      setState({ loader, data: null, error: '', loading: false });
      return () => { current = false; };
    }
    setState({ loader, data: null, error: '', loading: true });
    loader().then(data => {
      if (current) setState({ loader, data, error: '', loading: false });
    }).catch(error => {
      if (!current) return;
      if (onAccessError) onAccessError(error);
      setState({ loader, data: null, error: flagError(error, 'Unable to load this data.'), loading: false });
    });
    return () => { current = false; };
  }, [loader, onAccessError, version]);
  const reload = useCallback(() => setVersion(value => value + 1), []);
  return { ...(state.loader === loader ? state : { data: null, error: '', loading: Boolean(loader) }), reload };
};
export const ResourceState = ({ resource, children }) => <>
  {resource.loading && <ProgressBar mode="indeterminate" style={{ height: '6px' }} />}
  {resource.error && <div className="flex align-items-center gap-2 my-3">
    <Message severity="error" text={resource.error} /><Button label="Retry" onClick={resource.reload} />
  </div>}
  {!resource.loading && !resource.error && resource.data !== null && children}
</>;

const SiteContextUnavailable = ({ error }) => {
  const [tab, setTab] = useState(0);
  return <FlagPage title="Site Viewer">
    <Unavailable>{error.replace('BACKEND NOT YET AVAILABLE: ', '')}</Unavailable>
    <TabView activeIndex={tab} onTabChange={event => setTab(event.index)}>
      <TabPanel header="Member Search"><Unavailable>Member flag operations require server-authorized customer and location assignments. No arbitrary ID entry or browser-stored location selector is provided.</Unavailable></TabPanel>
      <TabPanel header="Allowed Scans"><Unavailable>Scan history authorized for site-user assignments and a live scan feed are not available.</Unavailable></TabPanel>
      <TabPanel header="Denied Scans"><Unavailable>Site-authorized scan history, triggered flag causes and role-sensitive event actions are not available.</Unavailable></TabPanel>
    </TabView>
  </FlagPage>;
};

const FlagSession = ({ children, customerOnly }) => {
  const { accessContext, isAuthReady } = useAuth();
  const { pathname, search } = useLocation();
  const account = flagAccount(accessContext);
  const capability = useCustomerFlagSystemCapability();
  const { accessIssue, handleAccessError } = usePageAccessRedirect({
    deniedTitle: 'Flag System Access Denied',
    deniedMessage: 'This account cannot access the requested customer, location or operation.',
    deniedRedirectTo: '/support',
  });
  const handleFeatureError = useCallback(error => {
    if (isFlagSystemDisabledError(error)) return true;
    return handleAccessError(error);
  }, [handleAccessError]);
  if (!isAuthReady) return <FlagPage title={customerOnly ? 'Flag System' : 'Site Viewer'}>
    <div role="status">Loading authenticated profile...</div>
  </FlagPage>;
  if (accessIssue) return <AccessState {...accessIssue} />;
  const requestedCustomer = new URLSearchParams(search).get('customerId');
  if (account.isCustomer && requestedCustomer !== null && Number(requestedCustomer) !== account.customerId) {
    return <Navigate to={pathname} replace />;
  }
  if (account.error || (customerOnly && !account.isCustomer)) {
    if (!customerOnly && account.error && account.error.startsWith('BACKEND NOT YET AVAILABLE:')) {
      return <SiteContextUnavailable error={account.error} />;
    }
    return <FlagPage title={customerOnly ? 'Flag System' : 'Site Viewer'}>
      {account.error && account.error.startsWith('BACKEND NOT YET AVAILABLE:') ?
        <Unavailable>{account.error.replace('BACKEND NOT YET AVAILABLE: ', '')}</Unavailable> :
        <Message severity="error" text={account.error || 'Customer Level access is required.'} />}
    </FlagPage>;
  }
  if (account.isCustomer && !capability.enabled) {
    return <FlagPage title="Flag System"><FlagCapabilityState capability={capability} /></FlagPage>;
  }
  return React.cloneElement(children, { account, onAccessError: handleFeatureError });
};
export const FlagSystemRoute = ({ children, customerOnly = false }) => {
  const { accessContext } = useAuth();
  // A new login/profile remounts all scoped state; no flag data is persisted or shared across accounts.
  const token = localStorage.getItem('token');
  const profile = useRef(accessContext);
  const generation = useRef(0);
  if (profile.current !== accessContext) {
    profile.current = accessContext;
    generation.current += 1;
  }
  return <AccessGate canAccess={canEnterFlagSystem} title="Flag System Access Required"
    message="Your account does not have access to this Flag System area." actionLabel="Go To Home" actionTo="/">
    <FlagSession key={`${token}:${generation.current}`} customerOnly={customerOnly}>{children}</FlagSession>
  </AccessGate>;
};
